import { useEffect, useMemo, useState } from "react";

import "./stylesDatePicker.css";

import { DatePicker as AntDatePicker } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { DATE_RANGE } from "../../../utils/Constants";
import { getEffectiveAdminTimezone } from "../../../utils/dateService";

const { RangePicker } = AntDatePicker;

dayjs.extend(utc);
dayjs.extend(timezone);

const generateDropdownClassName = () => {
  const baseClassName = "custom-dropdown-class";
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  return `${baseClassName}-${randomSuffix}`;
};

const matchesPreset = (preset, key) =>
  key != null && (preset.id === key || preset.label === key);

const presetKey = (preset) => preset.id ?? preset.label;

function DatePicker({
  getData,
  dates,
  setDates,
  showPresets = false,
  startRange = AppStrings.TODAY,
  presetOptions = null,
  allowClearToNull = false,
  picker = undefined,
}) {
  const [open, setOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(startRange);

  const dropdownClassName = useMemo(() => generateDropdownClassName(), []);
  const adminTimezone = getEffectiveAdminTimezone();

  const rangeValues = useMemo(
    () => ({
      currentMonth: [
        dayjs().tz(adminTimezone).startOf("month"),
        dayjs().tz(adminTimezone).endOf("day"),
      ],
      last30Days: [
        dayjs().tz(adminTimezone).subtract(29, "day").startOf("day"),
        dayjs().tz(adminTimezone).endOf("day"),
      ],
      last7Days: [
        dayjs().tz(adminTimezone).subtract(6, "day").startOf("day"),
        dayjs().tz(adminTimezone).endOf("day"),
      ],
      today: [
        dayjs().tz(adminTimezone).startOf("day"),
        dayjs().tz(adminTimezone).endOf("day"),
      ],
      yesterday: [
        dayjs().tz(adminTimezone).subtract(1, "day").startOf("day"),
        dayjs().tz(adminTimezone).subtract(1, "day").endOf("day"),
      ],
      thisWeek: [
        dayjs().tz(adminTimezone).startOf("week"),
        dayjs().tz(adminTimezone).endOf("week"),
      ],
      priorWeek: [
        dayjs().tz(adminTimezone).subtract(1, "week").startOf("week"),
        dayjs().tz(adminTimezone).subtract(1, "week").endOf("week"),
      ],
      thisMonth: [
        dayjs().tz(adminTimezone).startOf("month"),
        dayjs().tz(adminTimezone).endOf("month"),
      ],
      priorMonth: [
        dayjs().tz(adminTimezone).subtract(1, "month").startOf("month"),
        dayjs().tz(adminTimezone).subtract(1, "month").endOf("month"),
      ],
    }),
    [adminTimezone],
  );

  // Labels are read at render so TCM language switches apply. `id` is the
  // stable English DATE_RANGE / AppStrings default callers pass as startRange.
  const defaultQuickRanges = [
    {
      id: DATE_RANGE.CURRENT_MONTH,
      label: AppStrings.PAM_DATE_RANGE_CURRENT_MONTH,
      // Month-to-date: distinct from antd's THIS_MONTH preset (start →
      // end of month) — analytics never want a future-padded upper bound.
      value: rangeValues.currentMonth,
    },
    {
      id: DATE_RANGE.LAST_30_DAYS,
      label: AppStrings.PAM_DATE_RANGE_LAST_30_DAYS,
      value: rangeValues.last30Days,
    },
    {
      id: DATE_RANGE.LAST_7_DAYS,
      label: AppStrings.PAM_DATE_RANGE_LAST_7_DAYS,
      value: rangeValues.last7Days,
    },
    { id: AppStrings.TODAY, label: AppStrings.TODAY, value: rangeValues.today },
    {
      id: AppStrings.YESTERDAY,
      label: AppStrings.YESTERDAY,
      value: rangeValues.yesterday,
    },
    {
      id: AppStrings.THIS_WEEK,
      label: AppStrings.THIS_WEEK,
      value: rangeValues.thisWeek,
    },
    {
      id: AppStrings.PRIOR_WEEK,
      label: AppStrings.PRIOR_WEEK,
      value: rangeValues.priorWeek,
    },
    {
      id: AppStrings.THIS_MONTH,
      label: AppStrings.THIS_MONTH,
      value: rangeValues.thisMonth,
    },
    {
      id: AppStrings.PRIOR_MONTH,
      label: AppStrings.PRIOR_MONTH,
      value: rangeValues.priorMonth,
    },
  ];
  const quickRanges =
    presetOptions && presetOptions.length ? presetOptions : defaultQuickRanges;

  useEffect(() => {
    if (!dates || !Array.isArray(dates) || dates.length < 2) {
      if (startRange) {
        setSelectedPreset(startRange);
      }
      return;
    }

    const startStr = dates[0]?.format("YYYY-MM-DD");
    const endStr = dates[1]?.format("YYYY-MM-DD");
    const matchedPreset = quickRanges.find(
      (preset) =>
        startStr === preset.value[0].format("YYYY-MM-DD") &&
        endStr === preset.value[1].format("YYYY-MM-DD"),
    );

    if (matchedPreset) {
      setSelectedPreset(presetKey(matchedPreset));
    }
  }, [dates, startRange, quickRanges]);

  useEffect(() => {
    const presetList = document.querySelector(
      `.${dropdownClassName} .ant-picker-presets ul`,
    );
    if (presetList) {
      const listItems = presetList.querySelectorAll("li");
      listItems.forEach((li) => li.classList.remove("selected"));
      const selectedIndex = quickRanges.findIndex((p) =>
        matchesPreset(p, selectedPreset),
      );
      if (listItems[selectedIndex]) {
        listItems[selectedIndex].classList.add("selected");
      }
    }
  }, [selectedPreset, open, dropdownClassName, quickRanges]);

  return (
    <div
      className="custom-datepicker-container"
      data-testid="custom-datepicker-container"
    >
      <RangePicker
        picker={picker}
        value={dates}
        onChange={(values) => {
          setOpen(false);

          if (!values && allowClearToNull) {
            setDates(null);
            setSelectedPreset(null);
            getData(null);
            return;
          }

          let matchedPreset = null;

          if (!values) {
            matchedPreset =
              quickRanges.find((preset) => matchesPreset(preset, startRange)) ??
              quickRanges[0];
            if (!matchedPreset) {
              setDates(null);
              setSelectedPreset(null);
              getData(null);
              return;
            }
          } else {
            const startStr = values[0]?.format("YYYY-MM-DD");
            const endStr = values[1]?.format("YYYY-MM-DD");
            matchedPreset = quickRanges.find(
              (preset) =>
                startStr === preset.value[0].format("YYYY-MM-DD") &&
                endStr === preset.value[1].format("YYYY-MM-DD"),
            );
          }

          if (matchedPreset) {
            setSelectedPreset(presetKey(matchedPreset));
          }

          let queryString = "";
          if (values) {
            queryString = `start_date=${values[0].format(
              "YYYY-MM-DD",
            )}&end_date=${values[1].format("YYYY-MM-DD")}`;
            setDates(values);
          } else {
            queryString = `start_date=${matchedPreset.value[0].format(
              "YYYY-MM-DD",
            )}&end_date=${matchedPreset.value[1].format("YYYY-MM-DD")}`;
            setDates(matchedPreset.value);
          }
          getData(queryString);
        }}
        open={open}
        onOpenChange={setOpen}
        presets={showPresets ? quickRanges : []}
        placement="bottomRight"
        classNames={{ popup: { root: dropdownClassName } }}
      />
    </div>
  );
}

export default DatePicker;
